import { useMutation, useQuery } from "@tanstack/react-query"
import {
  ClientOnly,
  createFileRoute,
  useStableCallback,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  type ColumnDef,
  createColumnHelper,
  type VisibilityState,
} from "@tanstack/react-table"
import { PencilIcon, PlusIcon, PowerIcon } from "lucide-react"
import { useId, useMemo, useState } from "react"

import {
  ActionsDropdownMenu,
  type ActionsDropdownMenuGroup,
} from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { AddCurrencyModal } from "~/modules/system-config/components/add-currency-modal"
import { EditCurrencyModal } from "~/modules/system-config/components/edit-currency-modal"
import { updateCurrencyStatusFn } from "~/modules/system-config/system-config.functions"
import { getAvailableGlobalCurrenciesQueryOptions } from "~/modules/system-config/system-config.queries"
import type { Currency } from "~/modules/system-config/system-config.service"
import { ComponentErrorBoundary } from "~/root-components/component-error-boundary"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/_admin-layout/system-config/currencies",
)({
  component: RouteComponent,
})

const columnHelper = createColumnHelper<Currency>()
const TABLE_VISIBLE_COLUMNS_KEY = "system-config-currencies-visible-columns"

function RouteComponent() {
  const toastId = useId()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null,
  )
  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const { data: currencies = [], isLoading } = useQuery(
    getAvailableGlobalCurrenciesQueryOptions(["active", "inactive"]),
  )

  const updateCurrencyStatus = useServerFn(updateCurrencyStatusFn)

  const updateStatusMutation = useMutation({
    meta: {
      invalidates: [getAvailableGlobalCurrenciesQueryOptions().queryKey],
    },
    mutationFn: updateCurrencyStatus,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Currency status updated.", { id: toastId })
      } else {
        toast.error(result.message, { id: toastId })
      }
    },
    onError: (error) => {
      toast.error(error.message, { id: toastId })
    },
  })

  const getActionsGroups = useStableCallback(
    (currency: Currency): ActionsDropdownMenuGroup[] => [
      {
        actions: [
          {
            label: "Edit Currency",
            icon: PencilIcon,
            onClick: () => {
              setSelectedCurrency(currency)
              setShowEditModal(true)
            },
          },
        ],
      },
      {
        actions: [
          {
            label:
              currency.status === "active"
                ? "Disable Currency"
                : "Enable Currency",
            icon: PowerIcon,
            onClick: () => {
              toast.loading("Updating status...", { id: toastId })
              updateStatusMutation.mutate({
                data: {
                  code: currency.code,
                  status: currency.status === "active" ? "inactive" : "active",
                },
              })
            },
            variant:
              currency.status === "active"
                ? ("destructive" as const)
                : ("success" as const),
          },
        ],
      },
    ],
  )

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<Currency, any>[] = useMemo(
    () => [
      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="min-w-[80px]">Code</div>,
        cell: (info) => (
          <span className="font-mono text-white">{info.getValue()}</span>
        ),
        meta: { columnName: "Code" },
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="min-w-[120px]">Name</div>,
        cell: (info) => <span className="text-white">{info.getValue()}</span>,
        meta: { columnName: "Name" },
      }),
      columnHelper.accessor("symbol", {
        id: "symbol",
        header: () => <div className="min-w-[60px]">Symbol</div>,
        cell: (info) => (
          <span className="text-lg text-white">{info.getValue()}</span>
        ),
        meta: { columnName: "Symbol" },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: (info) => (
          <Badge
            variant={info.getValue() === "active" ? "default" : "secondary"}
            className={
              info.getValue() === "active"
                ? "bg-green-500/20 text-green-400"
                : "bg-gray-500/20 text-gray-400"
            }
          >
            {info.getValue() === "active" ? "Active" : "Disabled"}
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            { label: "Active", value: "active" },
            { label: "Disabled", value: "inactive" },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="grow text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <ActionsDropdownMenu groups={getActionsGroups(row.original)} />
          </div>
        ),
      }),
    ],
    [getActionsGroups],
  )

  return (
    <PageDetailsPanel
      title="Currencies"
      actions={
        <Button onClick={() => setShowAddModal(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Currency
        </Button>
      }
    >
      <div className="flex-1 overflow-auto p-3">
        <DataTable
          data={currencies}
          columns={columns}
          searchPlaceholder="Search currencies..."
          noResultsMessage={isLoading ? "Loading..." : "No currencies found"}
          manualPagination={false}
          manualSorting={false}
          manualFiltering={false}
          getInitialColumnVisibility={() => visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
        />
      </div>

      <ClientOnly>
        <ComponentErrorBoundary>
          <AddCurrencyModal
            open={showAddModal}
            onOpenChange={setShowAddModal}
          />

          {selectedCurrency && (
            <EditCurrencyModal
              key={selectedCurrency.code}
              open={showEditModal}
              onOpenChange={setShowEditModal}
              currency={selectedCurrency}
            />
          )}
        </ComponentErrorBoundary>
      </ClientOnly>
    </PageDetailsPanel>
  )
}
